import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { unwrap, useApi } from './api';
import { draftStore, useDraft } from './draft';

export type Entry = Record<string, unknown> & { id?: string };

/** Array collections in a bundle, with the columns shown in each table. */
export const collections = {
  items: {
    title: 'Items',
    description:
      'Weapons (damage, range, noise), ammo, armor, backpacks, tools, consumables, and materials.',
    columns: [
      ['Category', (e: Entry) => String(e.category ?? '')],
      ['kg', (e: Entry) => String(e.weightKg ?? '')],
      ['Noise m', (e: Entry) => (e.noiseRadiusM == null ? '' : String(e.noiseRadiusM))],
    ],
  },
  lootTables: {
    title: 'Loot tables',
    description:
      'Weighted loot per building category. Each roll picks one entry by weight, then a quantity.',
    columns: [['Entries', (e: Entry) => String((e.entries as unknown[] | undefined)?.length ?? 0)]],
  },
  recipes: {
    title: 'Recipes',
    description: 'Crafting at the homebase: inputs from base storage, outputs back into it.',
    columns: [
      [
        'Module',
        (e: Entry) =>
          e.requiredModuleId
            ? `${String(e.requiredModuleId)} ${String(e.requiredModuleLevel)}`
            : '-',
      ],
    ],
  },
  baseModules: {
    title: 'Base modules',
    description:
      'Workbench, Storage, Infirmary, Water collector: costs, build times, and effects per level.',
    columns: [['Levels', (e: Entry) => String((e.levels as unknown[] | undefined)?.length ?? 0)]],
  },
  zombies: {
    title: 'Zombies',
    description: 'Shambler, Runner, Brute: health, speed, hearing, sight, and damage.',
    columns: [
      ['HP', (e: Entry) => String(e.maxHealth ?? '')],
      ['Speed', (e: Entry) => String(e.moveSpeedMps ?? '')],
      ['Hearing', (e: Entry) => String(e.hearingMultiplier ?? '')],
    ],
  },
  categories: {
    title: 'Building categories',
    description: 'OpenStreetMap tags → game category → loot table, difficulty, and loot density.',
    columns: [
      ['Loot table', (e: Entry) => String(e.lootTableId ?? '')],
      ['Difficulty', (e: Entry) => String(e.difficulty ?? '')],
      ['Tags', (e: Entry) => String((e.osmTags as unknown[] | undefined)?.length ?? 0)],
    ],
  },
  winds: {
    // WIND (ADR 0073, contract 3.2): order = tier index; at least 2 rows, the first capacityMult 1, all non-increasing.
    title: 'Wind tiers',
    description:
      'The tiers of the wind tank (FRESH, WINDED, GASSED): order is the tier index. Each tier shrinks the tank and slows recovery, and raises the bite odds.',
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Capacity', (e: Entry) => `${String(e.capacityMult ?? 1)}x`],
      ['Recovery', (e: Entry) => `${String(e.recoveryMult ?? 1)}x`],
      ['Bite odds', (e: Entry) => `${String(e.biteOddsMult ?? 1)}x`],
    ],
  },
  roomTemplates: {
    title: 'Room templates',
    description:
      'Rooms the interior generator can place, per category, with containers and zombies.',
    columns: [
      ['Category', (e: Entry) => String(e.category ?? '')],
      ['Weight', (e: Entry) => String(e.weight ?? '')],
    ],
  },
  recruitRoles: {
    // Retired (stats replace roles, ADR 0044; card 11): nothing reads them and the validator refuses a non-empty list, so there is
    // no page (backgrounds[].legacyRole maps the old ids; crews, rescues and story recruits name background ids).
    title: 'Recruit roles (retired)',
    description: 'Retired: survivors have stats and backgrounds now. The list must stay empty.',
    columns: [['Effect', (e: Entry) => `${String(e.effect ?? '')} ${String(e.amount ?? '')}`]],
  },
  survivorTraits: {
    title: 'Survivor traits',
    description: 'Rare recruit traits (stragglers) that apply while the survivor is in a team.',
    // An absent field is the C# default (1x for multipliers, 0 for bonuses).
    columns: [
      ['Travel speed', (e: Entry) => `${String(e.travelSpeedMult ?? 1)}x`],
      ['Carry kg', (e: Entry) => String(e.carryKg ?? 0)],
      ['Noise', (e: Entry) => `${String(e.moveNoiseMult ?? 1)}x`],
      ['Max HP', (e: Entry) => String(e.maxHealthBonus ?? 0)],
    ],
  },
  bosses: {
    title: 'Bosses',
    description:
      'QR-code boss encounters (ADR 0028): the WANTED poster, the lifecycle, and the fight. Codes are on the Boss codes page.',
    // An absent field is the C# default (lib/teamsDefaults.ts bossDefaults).
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Status', (e: Entry) => String(e.status ?? 'coming_soon')],
      ['Playable', (e: Entry) => (e.playable ? 'yes' : 'no')],
      ['Difficulty', (e: Entry) => String(e.difficulty ?? 1)],
    ],
  },
  triggers: {
    title: 'Triggers',
    description:
      'Scan triggers (ADR 0029): what a scanned code does (start a fight, grant items, health, energy or a cosmetic, unlock a mission, start an event) and its rules. Codes are on the Trigger codes page.',
    // An absent field is the C# default (lib/teamsDefaults.ts triggerDefaults).
    columns: [
      ['Action', (e: Entry) => triggerActionLabel(e)],
      ['Status', (e: Entry) => `${String(e.status ?? 'coming_soon')}${e.devOnly ? ' · dev' : ''}`],
      [
        'Limit',
        (e: Entry) => {
          const r = (e.rules ?? {}) as { limit?: string; scope?: string };
          return `${r.limit ?? 'once'} / ${r.scope ?? 'run'}`;
        },
      ],
    ],
  },
  missions: {
    title: 'Missions',
    description:
      'The signed-in Missions page (web and app): a card per mission, fired by its trigger. Hidden missions appear once a start_mission trigger unlocks them. A mission with a Story block is a story issue (the Story tab).',
    columns: [
      ['Order', (e: Entry) => String(e.order ?? 0)],
      ['Trigger', (e: Entry) => String(e.triggerId ?? '')],
      [
        'Status',
        (e: Entry) =>
          `${String(e.status ?? 'coming_soon')} · ${String(e.visibility ?? 'listed')}${e.devOnly ? ' · dev' : ''}`,
      ],
    ],
  },
  cosmetics: {
    title: 'Cosmetics',
    description:
      'The cosmetics catalogue: granted by a grant_cosmetic trigger (owned, account-level) or by the supporter pass (its cosmeticIds must be listed here). Visual only.',
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Kind', (e: Entry) => String(e.kind ?? '')],
    ],
  },
  // Stories & Tech Step 1 (engine #124, panels #133, scenario #132).
  beatChains: {
    title: 'Beat chains',
    description:
      'Story beats: a tree of comic beats and timed choices with server-rolled hidden truths, outcome tones and effects. The editor shows the branch preview, tone mix and roll sheet; Simulate runs the real roller on the server.',
    wide: true,
    columns: [
      ['Title', (e: Entry) => String(e.title ?? '')],
      ['Status', (e: Entry) => String(e.status ?? 'live')],
      ['Beats', (e: Entry) => String(Object.keys((e.beats as object | undefined) ?? {}).length)],
    ],
  },
  allies: {
    title: 'Allies',
    description:
      'People met in a beat who send crates home (deliveries from a loot table) and come back in echo chains. Lost with the run.',
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      [
        'Deliveries',
        (e: Entry) => String((e.deliveries as { count?: number } | undefined)?.count ?? 3),
      ],
    ],
  },
  scenarios: {
    title: 'Scenarios',
    description:
      'Objectives, actors, scenario triggers (in-raid scripting, not scan triggers) and win/lose rules played inside an encounter. Dry run shows which objectives tick.',
    wide: true,
    columns: [
      ['Title', (e: Entry) => String(e.title ?? '')],
      ['Kind', (e: Entry) => String(e.kind ?? 'interior')],
      ['Objectives', (e: Entry) => String((e.objectives as unknown[] | undefined)?.length ?? 0)],
    ],
  },
  setPieces: {
    title: 'Set pieces',
    description:
      'Fixed rooms placed deterministically in an interior (the isolation ward, the dock cage): footprint, anchors, props and zombies.',
    wide: true,
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      [
        'Footprint',
        (e: Entry) => {
          const f = (e.footprint ?? {}) as { w?: number; d?: number };
          return `${f.w ?? 0}x${f.d ?? 0}`;
        },
      ],
    ],
  },
  worldFlags: {
    title: 'World flags',
    description:
      'The flag catalogue: which per-player keys beats and scenarios may write, their values and scope, and the map badge a flag shows.',
    idKey: 'key',
    columns: [
      ['Type', (e: Entry) => String(e.type ?? 'enum')],
      ['Scope', (e: Entry) => String(e.scope ?? 'run')],
      ['Badge', (e: Entry) => String(e.badge ?? '')],
    ],
  },
  npcs: {
    title: 'NPCs',
    description:
      'Named characters a beat or scenario casts (Nurse Okafor, Rosa): name, archetype, pronouns, look.',
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Archetype', (e: Entry) => String(e.archetype ?? '')],
    ],
  },
  pronounSets: {
    title: 'Pronoun sets',
    description:
      'she, he, they to start; more need no code. Used by text templates ({companion.their}).',
    columns: [['They', (e: Entry) => String(e.they ?? '')]],
  },
  // Stories & Tech Step 2 (occupied #125, holdout #127, crafting #134, MacGuffin #130).
  crews: {
    title: 'Crews',
    description:
      'Who lives in an occupied building: the OSM tags they settle in, disposition weights, roster, trade and stash tables, and their lines.',
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Roster', (e: Entry) => String((e.roster as unknown[] | undefined)?.length ?? 0)],
    ],
  },
  humans: {
    title: 'Humans',
    description:
      'Human NPC archetypes (leader, gunner, rusher, resident): health, weapon, the aim telegraph, hit chances, sight, morale.',
    columns: [
      ['HP', (e: Entry) => String(e.health ?? 70)],
      ['Weapon', (e: Entry) => String(e.weaponId || '-')],
      ['Telegraph s', (e: Entry) => String(e.telegraphS ?? 0.7)],
    ],
  },
  tells: {
    title: 'Tells',
    description:
      'Occupied-building clues shown at a gate (yard, defences, inside), each pointing at a hidden disposition. Yard tells can show on the map card.',
    columns: [
      ['Gate', (e: Entry) => String(e.gate ?? '')],
      ['Points to', (e: Entry) => String(e.disposition ?? '')],
      ['Map', (e: Entry) => (e.map ? 'yes' : '')],
    ],
  },
  holdouts: {
    title: 'Holdouts',
    description:
      'Sieges you defend: the set piece, setup and breather clocks, entries, the wave table, placement limits and squad scaling. The preview shows the waves per squad size against the live caps, the kill bound and the minimum duration.',
    wide: true,
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Waves', (e: Entry) => String((e.waves as unknown[] | undefined)?.length ?? 0)],
    ],
  },
  techTiers: {
    title: 'Tech tiers',
    description:
      'The workshop tiers: the gate facility, the key parts its build needs, the mastery milestones, the hunt mission and the blueprint rebuild recipes.',
    idKey: 'tier',
    numericId: true,
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Gate', (e: Entry) => String(e.gateModuleId ?? '')],
      ['Key parts', (e: Entry) => String((e.keyParts as unknown[] | undefined)?.length ?? 0)],
    ],
  },
  siteKinds: {
    title: 'Site kinds',
    description:
      'Where key parts can be: OSM tags that make a building this kind, the category it imports as, and whether it can be raided in person.',
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Category', (e: Entry) => String(e.category ?? '')],
      ['Tags', (e: Entry) => String((e.osmTags as unknown[] | undefined)?.length ?? 0)],
    ],
  },
  keyParts: {
    title: 'Key parts',
    description:
      'What a tier gate needs (MacGuffin): its pieces, the site kinds best first, fallbacks and virtual sites, the raid shape, the hint and beats.',
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Tier', (e: Entry) => String(e.tier ?? 0)],
      ['Pieces', (e: Entry) => String((e.pieces as unknown[] | undefined)?.length ?? 0)],
    ],
  },
  // Stories & Tech Step 3 (lure run #128).
  vehicles: {
    title: 'Vehicles',
    description:
      'The lent lure-run vehicles, the 5-step ladder (M6): the drive, arena handling, seats, noise, fuel, ram, clingers and the mount. The ladder table compares every rung: speed against a runner, noise radii as rings, the drive for 1 km.',
    wide: true,
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Seats', (e: Entry) => String((e.seats as unknown[] | undefined)?.length ?? 0)],
      ['Unlock', (e: Entry) => String(e.unlockAfterLureRuns ?? 0)],
    ],
  },
  arenaTemplates: {
    title: 'Arena templates',
    description:
      'Lure-run arenas: blocks, wreck and fuel-can slots, the objective, start and exit, and how the horde spreads. The top-down preview draws the shortest legal run line; problems show inline.',
    wide: true,
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Size', (e: Entry) => ((e.sizeM as number[] | undefined) ?? []).join(' x ')],
      ['Blocks', (e: Entry) => String((e.blocks as unknown[] | undefined)?.length ?? 0)],
    ],
  },
  // Stories & Tech Step 4 (story issues #131, ADR 0042).
  arcs: {
    title: 'Arcs',
    description:
      'Comic arcs on the shelf: the masthead logo, unlock, issues in reading order (missions with a Story block), the fixed cast, endings, coming-soon teasers, and the flags each issue writes and reads.',
    wide: true,
    columns: [
      ['Title', (e: Entry) => String(e.title ?? '')],
      [
        'Kind',
        (e: Entry) =>
          `${String(e.kind ?? 'oneoff')} · ${String(e.status ?? 'coming_soon')}${e.devOnly ? ' · dev' : ''}`,
      ],
      ['Issues', (e: Entry) => String((e.issues as unknown[] | undefined)?.length ?? 0)],
      ['Order', (e: Entry) => String(e.order ?? 0)],
    ],
  },
  // Outta-Ten (ADR 0066): stats and skills out of ten.
  coreStats: {
    title: 'Core stats',
    description:
      'The six core stats (STR, END, REF, WIT, NRV, CHA; ADR 0066): learning speed, rust class, what each level governs, the ability at 5, the perk at 7 and the signature at 10, and the info row (what it affects, how to raise it). 5 plays like today.',
    columns: [
      ['Short', (e: Entry) => String(e.short ?? '')],
      ['Rust', (e: Entry) => String(e.rustClass ?? '')],
      ['Governs', (e: Entry) => String((e.governs as unknown[] | undefined)?.length ?? 0)],
    ],
  },
  skills: {
    title: 'Skills',
    description:
      'Weapon, field and home skills (ADR 0066): baseline (the level that plays like today), learning speed, rust, what each level governs, and the 5 / 7 / 10 milestones.',
    columns: [
      ['Family', (e: Entry) => String(e.family ?? '')],
      ['Baseline', (e: Entry) => String(e.baseline ?? 0)],
      ['Rust', (e: Entry) => String(e.rustClass ?? '')],
    ],
  },
  perks: {
    title: 'Perks',
    description:
      'The perk definitions (ADR 0066, CONTRACT §13.4): each stat or skill names one as its ability7, which arrives at 7 with no choice. Each perk is modifiers on registered keys and behaviour flags.',
    columns: [['Owner', (e: Entry) => String(e.owner ?? '')]],
  },
  // The homebase redesign and lite-RPG stats (card 17).
  backgrounds: {
    title: 'Backgrounds',
    description:
      "Internal only, never shown to players (not roles): a recruit's starting stat bias, jacket and pick weight.",
    columns: [
      [
        'Bias',
        (e: Entry) =>
          Object.entries((e.bias as Record<string, number> | undefined) ?? {})
            .map(([k, v]) => `${k} ${v > 0 ? '+' : ''}${v}`)
            .join(' '),
      ],
      ['Weight', (e: Entry) => String(e.weight ?? 1)],
      ['Legacy role', (e: Entry) => String(e.legacyRole ?? '')],
    ],
  },
  // The Effects system (card 18, ADR 0045).
  statusEffects: {
    title: 'Effects',
    description:
      'Buffs and debuffs (ADR 0045): who they touch, modifiers on registered keys, duration, stacking, resist, the server events that apply them, and their text.',
    columns: [
      ['Polarity', (e: Entry) => String(e.polarity ?? 'buff')],
      ['Scope', (e: Entry) => String(e.scope ?? 'player')],
      [
        'Duration',
        (e: Entry) => String((e.duration as { kind?: string } | undefined)?.kind ?? 'timed'),
      ],
      [
        'Modifiers',
        (e: Entry) =>
          ((e.modifiers as { key?: string; value?: number }[] | undefined) ?? [])
            .map((m) => `${m?.key ?? ''} ${m?.value ?? ''}`)
            .join(', '),
      ],
    ],
  },
  zones: {
    title: 'Zones',
    description:
      'Designer places on the map that grant zone effects while the server believes the player is there. Draw a circle or a box, or type coordinates. Homes and outposts get their zones automatically.',
    wide: true,
    columns: [
      ['Kind', (e: Entry) => String(e.kind ?? 'place')],
      ['Shape', (e: Entry) => String(e.shape ?? 'polar')],
      ['Effects', (e: Entry) => ((e.effects as string[] | undefined) ?? []).join(', ')],
    ],
  },
  storageGroups: {
    title: 'Storage groups',
    description:
      "Inventory's shelves: each item goes to the first group (by order) that matches it, and 'other' takes the rest. Empty = the built-in groups.",
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Order', (e: Entry) => String(e.order ?? 0)],
    ],
  },
  buildCategories: {
    title: 'Build categories',
    description:
      "Build's item tabs (next level, base and other are the game's own). Empty = the built-in categories.",
    columns: [
      ['Name', (e: Entry) => String(e.name ?? '')],
      ['Order', (e: Entry) => String(e.order ?? 0)],
    ],
  },
} as const;

/** The field that names an entry: `id`, or `key` for the world flag catalogue. */
export const idKeyOf = (c: CollectionKey): string =>
  (collections[c] as { idKey?: string }).idKey ?? 'id';

/** Collections whose editor needs the full width (tree and grid editors). */
export const isWide = (c: CollectionKey): boolean => !!(collections[c] as { wide?: boolean }).wide;

/** Collections keyed by a number (tech tiers by `tier`): Add and Duplicate pick the next free number. */
export const isNumericId = (c: CollectionKey): boolean =>
  !!(collections[c] as { numericId?: boolean }).numericId;

/** "start_encounter: boss mother_hush", "grant_items: 2 stacks", ... for the triggers table. */
function triggerActionLabel(e: Entry): string {
  const action = String(e.action ?? '');
  const enc = e.encounter as { kind?: string; bossId?: string; encounterId?: string } | undefined;
  if (action === 'start_encounter' && enc)
    return `${action}: ${enc.kind ?? ''} ${enc.bossId ?? enc.encounterId ?? ''}`.trim();
  const items = e.items as { stacks?: unknown[] } | undefined;
  if (action === 'grant_items' && items) return `${action}: ${items.stacks?.length ?? 0} stack(s)`;
  return action;
}

/** Object sections edited as one JSON document. */
export const sections = {
  raidRules: {
    title: 'Raid rules',
    description:
      'Movement speeds, searching, doors, zombie spawn weights, the horde meter, headshots, and stealth.',
  },
  survival: {
    title: 'Survival rules',
    description: 'Upkeep, starter kit, distances, cooldowns, and recruiting limits.',
  },
  bases: {
    title: 'Bases and outposts',
    description:
      'Homebase and outposts (camps in the data; ADR 0030, ADR 0061): setting outposts up, the check-in ledger, home moves, expiry, and caravans.',
  },
  noiseRadii: {
    title: 'Noise radii',
    description: 'Meters per action (walk, sprint, pistol...). Weapons use their own noise.',
  },
  // Teams on the Map (F0): JSON editor only for now (see components/forms/registry.ts).
  teamRules: {
    title: 'Team rules',
    description:
      'Team slots and size, energy, travel speed, regen and fatigue tiers. (Leader effects are retired with roles.)',
  },
  discovery: {
    title: 'Discovery',
    description:
      'Destinations around the player, density tiers, weekly supply drops, and the tutorial drop.',
  },
  container: {
    title: 'Container',
    description:
      'When a location ping moves the anchor, and when a far jump counts as a relocation.',
  },
  convoy: {
    title: 'Convoy',
    description:
      'In-transit play: when a trip starts and ends, passed buildings, and queued orders.',
  },
  encounters: {
    title: 'Encounters',
    description: 'Route encounters rolled when a team travels: chance per km and each type.',
  },
  worldEvents: {
    title: 'World events',
    description:
      'Shared events per map cell and epoch: supply trucks, horde nests, distress calls.',
  },
  store: {
    title: 'Store',
    description:
      'Products and caps: bought energy, rewarded ads, the VIP pass (supporter subscription), and the resurrection ladder (kind resurrection, one product per tier; ADR 0069).',
  },
  // ADR 0069: characters, zombie mode and paid resurrection.
  characters: {
    title: 'Characters',
    description:
      'Character slots per account (zombies count), the VIP pass cap, and what the player types to delete one.',
  },
  resurrection: {
    title: 'Resurrection',
    description:
      'Paid un-zombie: the days-alive bands that set the price tier, the boost kit delivered on resurrection, and the cutscene.',
  },
  squadRules: {
    title: 'Squad rules',
    description:
      'Squad raids (ADR 0027): costs per extra member, downed and revive, and the AI teammates.',
  },
  story: {
    title: 'Story rules',
    description:
      'The beat engine numbers: depth, timers, roll-sheet size, the target tone mix, windfall caps, trades and allies.',
  },
  panelRules: {
    title: 'Panel rules',
    description:
      'Live comic panels: actors and balloons per cell, layout, swipe, quality tiers and shot framing.',
  },
  // Stories & Tech Step 2.
  chanceEvents: {
    title: 'Chance events',
    description:
      'Moral choices that fire mid-raid: how often (rules), and each event: the beat chain it plays, eligibility and trigger. The path matrix shows every option against every hidden roll.',
  },
  occupied: {
    title: 'Occupied buildings',
    description:
      'How often a destination is occupied, the zombie multiplier, tell rolls, the disposition x approach stance grid, trades and recruits. The preview rolls tells and stance odds for a crew.',
  },
  rescues: {
    title: 'Rescues',
    description:
      'Rescue raids: caps, the Radio LISTEN calls, escort numbers, name pools, and the rescue scenarios with the wave clock preview and kill bound.',
  },
  holdoutRules: {
    title: 'Holdout rules',
    description:
      'Live zombie caps by device tier, corpses, nav area costs, spill queue, stragglers and door bashing hit points.',
  },
  crafting: {
    title: 'Crafting',
    description:
      'The workshop: craft seconds per tier, build-order caps, the delivery banner, power (fuel) and the tool wall.',
  },
  fieldKit: {
    title: 'Field kit',
    description:
      "The phone's field-craft card: which field recipes it lists and the most made at once.",
  },
  keyPartRules: {
    title: 'Key-part rules',
    description:
      'The haul weight limit, site radii and timers, haul class numbers, and the virtual (synthetic) sites.',
  },
  // Stories & Tech Step 3 (lure run #128, sniper #129).
  arenaRules: {
    title: 'Arena rules',
    description:
      'The lure-run arena numbers: time limit, horde cap and device tiers, reinforcements, the grab and pick-up, fuel cans, losing track, claw damage and the crowd tier.',
  },
  lureSites: {
    title: 'Lure sites',
    description:
      'Lure-run destinations on the map (M7): the on switch, chance and cap per set, the window, distance, categories, horde by density tier, starting vehicles, the lent vehicle weights, the Overrun share and the cargo.',
  },
  overwatch: {
    title: 'Overwatch',
    description:
      'Sniper missions: rules, scenes with their perches and the imported plan, figures, trait pools, missions and phases. The plan preview plays the timelines of a mission from a seed.',
  },
  // Stories & Tech Step 4 (story issues #131).
  storyRules: {
    title: 'Story issue rules',
    description:
      'Story issues (ADR 0042): the on switch, the story ticket lifetime, the location fallbacks (story drops, sameAs, picked) and when the reader cutscene shortens.',
  },
  // Day Zero, the tutorial (ADR 0074).
  tutorialRules: {
    title: 'Day Zero rules',
    description:
      'The tutorial (ADR 0074, tutorial contract 7.5): the release date the backfill reads, the hero tank, the WIND floor (windFloor, 0..heroTank-10: the hero bar never drops below it in Day Zero), the layouts, the openers (companion, closing line, hours), the run (the street chase, v2.1: belowPct unseals the safe door, packSize, holdBackM, closeMps, caughtGrabs), lock, wave and lunge numbers, the hint ladder and the collapse distance. JSON for now.',
  },
  conditions: {
    title: 'Conditions',
    description:
      'Injuries and sickness (ADR 0031): the on/off switch, causes, recovery cuts, each condition with its stat penalties and cures.',
  },
  // The homebase redesign and lite-RPG stats (card 17).
  skillRules: {
    title: 'Skill rules',
    description:
      'Outta-Ten learning and rust (ADR 0066): the rate curve, daily caps, challenge, rust classes, awards, workouts into the body, starting swaps and the limits. Stamina is under Raid rules.',
  },
  names: {
    title: 'Names',
    description:
      'Real names for NPCs and recruits (CONTRACT §13.5): male and female first names and last names, 1960s American. The Stranger, raid recruits, stragglers, occupied crews and dev survivors get one; story cast and QR copies keep theirs.',
  },
  effectRules: {
    title: 'Effect rules',
    description:
      'The Effects numbers (ADR 0045): max active per target, the stat cap, per-key clamps, zone defaults, and the effects homes and outposts grant.',
  },
  healthRules: {
    title: 'Health rules',
    description:
      'Health intake (Apple Health / Health Connect workouts): the switches, import window, daily caps, tier credit, intensity, plausibility bounds, day bands, weekly levels and the trusted source apps.',
  },
  homeRules: {
    title: 'Home rules',
    description:
      'The homebase view (ADR 0046): infirmary admission, home training marks, the headline, supply thresholds, and list sizes.',
  },
  inventoryRules: {
    title: 'Inventory rules',
    description:
      'The limit switches (ADR 0048): carry, storage and loadout limits, and whether the field kit uses the haul. No block = every limit on.',
  },
  loadoutRules: {
    title: 'Loadout slots',
    description:
      "The loadout's slots and what each accepts, and the goods limit (ADR 0048 §7). No block = weapon 1, weapon 2, armor, backpack.",
  },
} as const;

export type CollectionKey = keyof typeof collections;
export type SectionKey = keyof typeof sections;

/** Loads the published bundle into a draft the first time an editor opens. */
export function useEnsureDraft() {
  const { client } = useApi();
  const draft = useDraft();
  const current = useQuery({
    queryKey: ['content-current'],
    queryFn: async () => unwrap(await client.GET('/content/current')),
    enabled: !draft,
  });
  const bundle = useQuery({
    queryKey: ['admin-content', current.data?.version],
    queryFn: async () =>
      unwrap(
        await client.GET('/admin/content/{version}', {
          params: { path: { version: current.data?.version ?? 0 } },
        }),
      ),
    enabled: !draft && current.data !== undefined,
  });
  useEffect(() => {
    if (!draft && bundle.data)
      draftStore.set({ baseVersion: bundle.data.version ?? 0, bundle: bundle.data });
  }, [draft, bundle.data]);
  return {
    draft,
    loading: !draft && (current.isPending || bundle.isPending),
    error: current.error ?? bundle.error,
  };
}
